import { Link } from "react-router-dom";

export default function ForeclosedHome() {
  return (
    <div className="min-h-screen bg-[#080808] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#080808]/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 bg-gradient-to-br from-[#00E5FF] to-[#00B8D4] rounded flex items-center justify-center">
              <i className="ri-building-4-line text-xl text-[#080808]"></i>
            </div>
            <span className="font-['Barlow_Condensed'] text-2xl font-bold tracking-wide">SURE KLEEN</span>
          </Link>
          <Link to="/" className="text-sm font-['Space_Grotesk'] text-white/70 hover:text-[#00E5FF] transition-colors whitespace-nowrap">
            <i className="ri-arrow-left-line mr-2"></i>Back to Home
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden mt-16">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/e627166ce1a9e4834725d4c9c1055b6f.jpg"
            alt="Foreclosed Home Cleaning"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#080808]"></div>
        </div>
        <div className="relative z-10 text-center max-w-4xl px-6">
          <div className="inline-block px-4 py-2 bg-[#00E5FF]/10 border border-[#00E5FF]/30 rounded-full mb-6">
            <span className="font-['Space_Mono'] text-sm text-[#00E5FF] tracking-wider">SIC 7349</span>
          </div>
          <h1 className="font-['Barlow_Condensed'] text-6xl md:text-7xl font-bold mb-6 tracking-tight">
            Foreclosed Home Cleaning
          </h1>
          <p className="font-['Space_Grotesk'] text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">
            Property preservation and restoration services for banks, REO departments, and asset managers
          </p>
        </div>
      </section>

      {/* What We Deliver */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-['Barlow_Condensed'] text-5xl font-bold mb-16 text-center">
            What We <span className="text-[#00E5FF]">Deliver</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: "ri-delete-bin-line",
                title: "Complete Trash-Out",
                desc: "Removal of all personal property, debris, and abandoned items with proper disposal"
              },
              {
                icon: "ri-paint-brush-line",
                title: "Deep Cleaning",
                desc: "Comprehensive interior cleaning including appliances, fixtures, and all surfaces"
              },
              {
                icon: "ri-leaf-line",
                title: "Exterior Maintenance",
                desc: "Lawn mowing, debris removal, and curb appeal restoration"
              },
              {
                icon: "ri-tools-line",
                title: "Minor Repairs",
                desc: "Securing doors/windows, changing locks, and addressing safety hazards"
              },
              {
                icon: "ri-camera-line",
                title: "Photo Documentation",
                desc: "Before/after photography and detailed condition reports for asset managers"
              },
              {
                icon: "ri-time-line",
                title: "Rapid Turnaround",
                desc: "Fast response times to meet listing deadlines and market-ready requirements"
              }
            ].map((item, idx) => (
              <div key={idx} className="bg-white/5 border border-white/10 rounded-lg p-8 hover:border-[#00E5FF]/50 transition-all group">
                <div className="w-14 h-14 bg-[#00E5FF]/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#00E5FF]/20 transition-colors">
                  <i className={`${item.icon} text-3xl text-[#00E5FF]`}></i>
                </div>
                <h3 className="font-['Space_Grotesk'] text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-white/70 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="py-24 px-6 bg-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-['Barlow_Condensed'] text-5xl font-bold mb-8">
            Why It <span className="text-[#00E5FF]">Matters</span>
          </h2>
          <p className="font-['Space_Grotesk'] text-lg text-white/80 leading-relaxed mb-6">
            Foreclosed properties often sit vacant for months, accumulating damage, debris, and deferred maintenance. Banks and asset managers need reliable partners who can quickly transform distressed properties into market-ready assets.
          </p>
          <p className="font-['Space_Grotesk'] text-lg text-white/80 leading-relaxed">
            Sure Kleen specializes in REO property preservation—handling everything from initial trash-out to final cleaning, with the documentation and turnaround speed that financial institutions require.
          </p>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-['Barlow_Condensed'] text-5xl font-bold mb-16 text-center">
            Our <span className="text-[#00E5FF]">Process</span>
          </h2>
          <div className="space-y-8">
            {[
              {
                step: "01",
                title: "Initial Assessment",
                desc: "Property walkthrough with photo documentation and scope-of-work development"
              },
              {
                step: "02",
                title: "Trash-Out & Debris Removal",
                desc: "Complete removal of personal property, furniture, and accumulated debris"
              },
              {
                step: "03",
                title: "Interior Cleaning",
                desc: "Deep cleaning of all rooms, appliances, fixtures, and surfaces to move-in condition"
              },
              {
                step: "04",
                title: "Exterior Restoration",
                desc: "Lawn care, debris removal, and curb appeal improvements"
              },
              {
                step: "05",
                title: "Final Documentation",
                desc: "After photos, completion report, and invoice submission to asset manager"
              }
            ].map((item, idx) => (
              <div key={idx} className="flex gap-8 items-start group">
                <div className="flex-shrink-0 w-20 h-20 bg-gradient-to-br from-[#00E5FF] to-[#00B8D4] rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <span className="font-['Barlow_Condensed'] text-2xl font-bold text-[#080808]">{item.step}</span>
                </div>
                <div className="flex-1 pt-2">
                  <h3 className="font-['Space_Grotesk'] text-2xl font-bold mb-3">{item.title}</h3>
                  <p className="text-white/70 leading-relaxed text-lg">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Standards & Compliance */}
      <section className="py-24 px-6 bg-white/5">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-['Barlow_Condensed'] text-5xl font-bold mb-16 text-center">
            Standards & <span className="text-[#00E5FF]">Compliance</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: "ri-government-line",
                title: "SBA 8(a) Certified",
                desc: "Qualified for government-backed REO property preservation contracts"
              },
              {
                icon: "ri-shield-check-line",
                title: "Bonded & Insured",
                desc: "Full liability coverage for property access and preservation work"
              },
              {
                icon: "ri-file-list-3-line",
                title: "Vendor Compliance",
                desc: "Registered with major REO platforms and asset management systems"
              }
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="w-20 h-20 bg-[#00E5FF]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${item.icon} text-4xl text-[#00E5FF]`}></i>
                </div>
                <h3 className="font-['Space_Grotesk'] text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-white/70 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-['Barlow_Condensed'] text-5xl font-bold mb-6">
            Turn Distressed Assets into Market-Ready Properties
          </h2>
          <p className="font-['Space_Grotesk'] text-xl text-white/80 mb-10">
            Partner with Sure Kleen for reliable REO property preservation services.
          </p>
          <a
            href="/#contact"
            className="inline-block px-10 py-4 bg-gradient-to-r from-[#00E5FF] to-[#00B8D4] text-[#080808] font-['Space_Grotesk'] font-bold text-lg rounded-lg hover:shadow-[0_0_30px_rgba(0,229,255,0.5)] transition-all whitespace-nowrap"
          >
            Request Consultation
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="font-['Space_Mono'] text-sm text-white/50">
            © 2026 Sure Kleen Services. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}